'use client'

/**
 * StarRating — a 1–5 star control that works as both a display and an
 * interactive input. Filled stars use the brand `--star-rating` gold;
 * empties are muted. Hover previews the value while interacting.
 *
 * Accessible: interactive mode renders a radiogroup of labelled buttons
 * so keyboard + screen-reader users can set a rating.
 */
import { useState } from 'react'
import { cn } from '@/lib/utils'

const SIZES = {
  sm: 'text-base',
  md: 'text-xl',
  lg: 'text-[28px]',
} as const

export function StarRating({
  value,
  onChange,
  readOnly = false,
  size = 'md',
  className,
  'aria-label': ariaLabel = 'Rating',
}: {
  value: number
  onChange?: (rating: number) => void
  readOnly?: boolean
  size?: keyof typeof SIZES
  className?: string
  'aria-label'?: string
}) {
  const [hover, setHover] = useState(0)
  const interactive = !readOnly && !!onChange
  const shown = hover || value

  return (
    <div
      className={cn('flex gap-0.5', className)}
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={interactive ? ariaLabel : `${ariaLabel}: ${value} out of 5`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          role={interactive ? 'radio' : undefined}
          aria-checked={interactive ? value === star : undefined}
          aria-label={interactive ? `${star} star${star === 1 ? '' : 's'}` : undefined}
          tabIndex={interactive ? 0 : -1}
          className={cn(
            SIZES[size],
            'leading-none transition-opacity',
            interactive
              ? 'cursor-pointer hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
              : 'cursor-default',
          )}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          onFocus={() => interactive && setHover(star)}
          onBlur={() => interactive && setHover(0)}
          onClick={() => interactive && onChange?.(star)}
        >
          <span className={shown >= star ? 'text-star-rating' : 'text-[#D9DCE6]'}>★</span>
        </button>
      ))}
    </div>
  )
}
